import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Shield, Crown, FileText, DollarSign, Landmark, Tags, Users, ClipboardList,
  Plus, Pencil, Trash2, KeyRound
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const permissionLabels = {
  can_manage_statements: { label: 'Manage Statements', icon: FileText, desc: 'Generate, upload, view statements' },
  can_view_finance: { label: 'View Finance', icon: DollarSign, desc: 'Access finance dashboard and expenses' },
  can_import_bank: { label: 'Import Banking', icon: Landmark, desc: 'Import bank statements, view transactions' },
  can_edit_rules: { label: 'Edit Rules', icon: Tags, desc: 'Create/delete category rules' },
  can_manage_drivers: { label: 'Manage Drivers', icon: Users, desc: 'Create, edit, deactivate drivers' },
  can_manage_registrations: { label: 'Manage Registrations', icon: ClipboardList, desc: 'Approve/reject registrations' },
};

export default function AdminPermissions() {
  const { user: currentUser } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletingAdmin, setDeletingAdmin] = useState(null);
  const [form, setForm] = useState({ username: '', email: '', full_name: '', password: '' });

  const isMasterUser = currentUser?.permissions?.is_master_admin || currentUser?.email === 'admin@jsmtrading.com';

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/all-admins`);
      setAdmins(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load admins'));
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = async (adminId, permissionKey, currentValue) => {
    setUpdating(prev => ({ ...prev, [`${adminId}-${permissionKey}`]: true }));
    
    try {
      await axios.put(`${API_URL}/admin/permissions/${adminId}`, {
        [permissionKey]: !currentValue
      });
      toast.success('Permission updated');
      // Update local state
      setAdmins(prev => prev.map(admin => {
        if (admin.id === adminId) {
          return {
            ...admin,
            permissions: {
              ...admin.permissions,
              [permissionKey]: !currentValue
            }
          };
        }
        return admin;
      }));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update permission'));
    } finally {
      setUpdating(prev => ({ ...prev, [`${adminId}-${permissionKey}`]: false }));
    }
  };

  const resetForm = () => setForm({ username: '', email: '', full_name: '', password: '' });

  const handleCreate = async () => {
    if (!form.username || !form.full_name || !form.password) {
      toast.error('Username, name, and password required');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/admins`, { ...form, email: form.email || form.username });
      toast.success('Admin created!');
      setShowAdd(false);
      resetForm();
      fetchAdmins();
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to create admin')); }
  };

  const handleUpdate = async () => {
    if (!editing) return;
    try {
      const payload = {
        full_name: editing.full_name,
        email: editing.email,
        is_active: editing.is_active,
      };
      if (editing._newPassword) payload.password = editing._newPassword;
      await axios.put(`${API_URL}/admin/admins/${editing.id}`, payload);
      toast.success('Admin updated!');
      setEditing(null);
      fetchAdmins();
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to update')); }
  };

  const handleDelete = async () => {
    if (!deletingAdmin) return;
    try {
      await axios.delete(`${API_URL}/admin/admins/${deletingAdmin.id}`);
      toast.success('Admin deleted');
      setDeletingAdmin(null);
      fetchAdmins();
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to delete')); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-permissions">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Admin Users & Permissions</h1>
            <p className="text-muted-foreground mt-1">Create admin accounts and control what each admin can access</p>
          </div>
          {isMasterUser && (
            <Button onClick={() => setShowAdd(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-admin-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Admin
            </Button>
          )}
        </div>

        {/* Legend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Permission Types
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(permissionLabels).map(([key, { label, icon: Icon, desc }]) => (
                <div key={key} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <Icon className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">{label}</p>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Admins Table */}
        <Card>
          <CardHeader>
            <CardTitle>Admin Users</CardTitle>
            <CardDescription>Toggle permissions for each admin</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading...</div>
            ) : admins.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium">No admins found</h3>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Admin</TableHead>
                      {Object.entries(permissionLabels).map(([key, { label }]) => (
                        <TableHead key={key} className="text-center">{label}</TableHead>
                      ))}
                      {isMasterUser && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {admins.map((admin) => {
                      const isMaster = admin.permissions?.is_master_admin || admin.email === 'admin@jsmtrading.com';
                      
                      return (
                        <TableRow key={admin.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {isMaster && <Crown className="h-4 w-4 text-amber-500" />}
                              <div>
                                <p className="font-medium">{admin.full_name}</p>
                                <p className="text-xs text-muted-foreground">{admin.email}</p>
                              </div>
                              {isMaster && (
                                <Badge className="bg-amber-100 text-amber-700">Master</Badge>
                              )}
                            </div>
                          </TableCell>
                          {Object.keys(permissionLabels).map((key) => (
                            <TableCell key={key} className="text-center">
                              {isMaster ? (
                                <Badge className="bg-green-100 text-green-700">All Access</Badge>
                              ) : (
                                <Switch
                                  checked={admin.permissions?.[key] ?? true}
                                  onCheckedChange={() => handleTogglePermission(
                                    admin.id, 
                                    key, 
                                    admin.permissions?.[key] ?? true
                                  )}
                                  disabled={updating[`${admin.id}-${key}`]}
                                />
                              )}
                            </TableCell>
                          ))}
                          {isMasterUser && (
                            <TableCell className="text-right">
                              {isMaster ? (
                                <span className="text-xs text-slate-400">protected</span>
                              ) : (
                                <div className="flex gap-1 justify-end">
                                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...admin, _newPassword: '' })} data-testid={`edit-admin-${admin.id}`}>
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                  <Button size="sm" variant="destructive" onClick={() => setDeletingAdmin(admin)} data-testid={`delete-admin-${admin.id}`}>
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Crown className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">Master Admin</p>
                <p className="text-sm text-blue-700">
                  The master admin (admin@jsmtrading.com) has full access to all features and cannot have permissions restricted.
                  Only master admin can modify other admins' permissions.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add Admin Dialog */}
        <Dialog open={showAdd} onOpenChange={(o) => { setShowAdd(o); if (!o) resetForm(); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Admin</DialogTitle>
              <DialogDescription>Create a new admin user with full default permissions. You can restrict permissions after creation.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Username *</Label><Input value={form.username} onChange={e => setForm({...form, username: e.target.value})} placeholder="e.g., john_admin" data-testid="admin-username" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="john@jsmt.co.za (optional)" /></div>
              <div><Label>Full Name *</Label><Input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} placeholder="John Smith" data-testid="admin-fullname" /></div>
              <div><Label>Password * (min 6 chars)</Label><Input type="text" value={form.password} onChange={e => setForm({...form, password: e.target.value})} data-testid="admin-password" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowAdd(false); resetForm(); }}>Cancel</Button>
              <Button onClick={handleCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-admin-btn">Create Admin</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Admin Dialog */}
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Admin — {editing?.username}</DialogTitle></DialogHeader>
            {editing && (
              <div className="space-y-3">
                <div><Label>Full Name</Label><Input value={editing.full_name} onChange={e => setEditing({...editing, full_name: e.target.value})} /></div>
                <div><Label>Email</Label><Input type="email" value={editing.email} onChange={e => setEditing({...editing, email: e.target.value})} /></div>
                <div><Label className="flex items-center gap-1"><KeyRound className="h-3 w-3" /> Reset Password (leave blank to keep)</Label><Input type="text" value={editing._newPassword} onChange={e => setEditing({...editing, _newPassword: e.target.value})} placeholder="New password" /></div>
                <div className="flex items-center justify-between border-t pt-3"><Label>Account Active</Label><Switch checked={editing.is_active} onCheckedChange={v => setEditing({...editing, is_active: v})} /></div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={handleUpdate} className="bg-indigo-600 hover:bg-indigo-700">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Admin Dialog */}
        <Dialog open={!!deletingAdmin} onOpenChange={(o) => !o && setDeletingAdmin(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Admin</DialogTitle>
              <DialogDescription>Are you sure you want to delete this admin? This cannot be undone.</DialogDescription>
            </DialogHeader>
            {deletingAdmin && (
              <div className="bg-red-50 border border-red-200 rounded p-3 text-sm">
                <p className="font-semibold">{deletingAdmin.full_name}</p>
                <p className="text-slate-600">{deletingAdmin.email}</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeletingAdmin(null)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-admin">Delete Admin</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
